import { MouseEvent, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useHistory } from 'react-router-dom';
import { Query, QueryResult } from '@material-table/core';
import {
  OverviewApiQuery,
  OverviewBatch,
  OverviewCounts,
} from '../../services/service-evaluations';
import {
  EvaluationOverviewSortValue,
  EvaluationSortDirection,
  EvaluationSortDirectionValue,
} from '../../util/evaluationContract';
import { readEvaluations } from '../../util/evaluationsCache';
import { EvaluationsView, WizardSeed } from '../../util/evaluationsNavigation';
import { OverviewQuery, OverviewSort, filterKeyOf, toApiQuery } from './overviewQuery';

/** What the shell needs from a loaded page, whichever view loaded it. */
export type OverviewMeta = {
  counts: OverviewCounts;
  batch: OverviewBatch | null;
  focus: { supplierId: string; name: string } | null;
  totalCount: number;
};

export type OverviewTableProps = {
  query: OverviewQuery;
  canWrite: boolean;
  /** The query is the one the page was loaded for, which can be behind the URL. */
  onLoaded: (meta: OverviewMeta, query: OverviewQuery) => void;
  onFailed: (error: unknown, query: OverviewApiQuery) => void;
  onOpenChange: (open: string | null) => void;
  /** 1-based, as the URL shows it. */
  onPageChange: (page: number) => void;
  /** 1-based: the last page, shown in place of a page past it. Keeps the open row. */
  onPageRestored: (page: number) => void;
  /** None is the default, latest activity first. */
  onSortChange: (sort: OverviewSort | null) => void;
  onStart: (seed: WizardSeed) => void;
};

/** The column field each sort the view offers is shown under. */
export type SortFields = Partial<Record<EvaluationOverviewSortValue, string>>;

type PanelState = { tableData?: { showDetailPanel?: unknown; uuid?: string } };

// material-table exposes no ref type; these are the members the overview drives.
export type TableHandle = {
  onQueryChange: (query?: { page?: number }) => void;
  onToggleDetailPanel: (path: number[], render: unknown) => void;
  dataManager?: { sortedData?: PanelState[] };
};

type PageShape = { page: number; pageSize: number; totalCount: number };

type Options<Page extends PageShape, Row extends object> = {
  view: EvaluationsView;
  query: OverviewQuery;
  load: (query: OverviewApiQuery) => Promise<Page>;
  rowsOf: (page: Page) => Row[];
  keyOf: (row: Row) => string;
  renderPanel: (props: { rowData: Row }) => JSX.Element;
  onLoaded: (page: Page, query: OverviewQuery) => void;
  onFailed: (error: unknown, query: OverviewApiQuery) => void;
  onOpenChange: (open: string | null) => void;
  onPageRestored: (page: number) => void;
  onSortChange: (sort: OverviewSort | null) => void;
  sortFields: SortFields;
};

type OrderEntry = { orderDirection?: string; sortOrder?: number; orderByField?: string };

const SORT_DIRECTIONS: string[] = Object.values(EvaluationSortDirection);

const sortOfField = (
  sortFields: SortFields,
  field: string | undefined,
  dir: string | undefined,
): OverviewSort | null => {
  const key = (Object.keys(sortFields) as EvaluationOverviewSortValue[]).find(
    sortKey => sortFields[sortKey] === field,
  );
  return key && dir && SORT_DIRECTIONS.includes(dir)
    ? { key, dir: dir as EvaluationSortDirectionValue }
    : null;
};

// The header is the truth for a fetch: a click asks for its rows before it reports the new order.
const tableSortOf = <Row extends object>(sortFields: SortFields, tableQuery: Query<Row>) => {
  const entries = (tableQuery.orderByCollection ?? []) as OrderEntry[];
  const active = entries.find(entry => entry.sortOrder && entry.orderDirection);
  return active ? sortOfField(sortFields, active.orderByField, active.orderDirection) : null;
};

const viewSortOf = (sortFields: SortFields, query: OverviewQuery) =>
  query.sort && sortFields[query.sort.key] ? query.sort : null;

// material-table keeps whichever request settles last, so each one settles with the newest rows.
const settleLatest = <T>(
  latest: MutableRefObject<Promise<T> | null>,
  request: Promise<T>,
): Promise<T> =>
  request.then(result =>
    latest.current && latest.current !== request ? settleLatest(latest, latest.current) : result,
  );

export const lastPageIndex = (totalCount: number, pageSize: number) =>
  Math.max(0, Math.ceil(totalCount / pageSize) - 1);

export const isPanelOpen = (row: object) => Boolean((row as PanelState).tableData?.showDetailPanel);

// A row opened through tableData is unknown to material-table's single-panel bookkeeping.
export const closeOtherPanels = (table: TableHandle | null, clicked: object, render: unknown) => {
  const uuid = (clicked as PanelState).tableData?.uuid;
  (table?.dataManager?.sortedData ?? []).forEach((row, index) => {
    if (row.tableData?.uuid !== uuid && row.tableData?.showDetailPanel) {
      table?.onToggleDetailPanel([index], render);
    }
  });
};

// The URL's page is restored on the first load, its open row on every load.
export const useRemoteOverview = <Page extends PageShape, Row extends object>({
  view,
  query,
  load,
  rowsOf,
  keyOf,
  renderPanel,
  onLoaded,
  onFailed,
  onOpenChange,
  onPageRestored,
  onSortChange,
  sortFields,
}: Options<Page, Row>) => {
  const history = useHistory();
  const tableRef = useRef<TableHandle | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const queryRef = useRef(query);
  queryRef.current = query;
  const restorePending = useRef(true);
  const callbacks = useRef({ onLoaded, onFailed, onOpenChange, onPageRestored, onSortChange });
  callbacks.current = { onLoaded, onFailed, onOpenChange, onPageRestored, onSortChange };

  const latestRequest = useRef<Promise<QueryResult<Row>> | null>(null);
  const requestSeq = useRef(0);
  const skipCache = useRef(false);
  // The changed answer that asked the table again, handed to that re-query so it cannot loop.
  const requeried = useRef<{ query: OverviewApiQuery; page: Page } | null>(null);

  // A table that a view switch, a retry or leaving the overview unmounted must not report to the shell.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (tableQuery: Query<Row>): Promise<QueryResult<Row>> => {
      const seq = ++requestSeq.current;
      const stale = () => seq !== requestSeq.current || !mounted.current;
      const current = queryRef.current;
      const restoring = restorePending.current;
      const skip = skipCache.current;
      skipCache.current = false;
      // The cached page answers at once; a different answer from the server re-queries the table.
      const loadPage = async (pageQuery: OverviewApiQuery) => {
        const handed = requeried.current;
        requeried.current = null;
        if (handed && isEqual(handed.query, pageQuery)) return handed.page;
        const read = readEvaluations(`overview:${view}`, pageQuery, () => load(pageQuery), {
          skipCache: skip,
        });
        if (read.cached === undefined) return (await read.fresh).value;
        read.fresh.then(
          ({ value, changed }) => {
            if (!changed || stale()) return;
            requeried.current = { query: pageQuery, page: value };
            tableRef.current?.onQueryChange();
          },
          () => undefined,
        );
        return read.cached;
      };
      let apiQuery = toApiQuery(
        current,
        view,
        restoring ? current.page - 1 : tableQuery.page,
        tableSortOf(sortFields, tableQuery),
      );
      try {
        let page = await loadPage(apiQuery);
        const pastLastPage = restoring && page.totalCount > 0 && !rowsOf(page).length;
        if (pastLastPage && !stale()) {
          apiQuery = { ...apiQuery, page: lastPageIndex(page.totalCount, page.pageSize) };
          page = await loadPage(apiQuery);
        }
        if (stale()) return { data: [], page: 0, totalCount: 0 };
        restorePending.current = false;
        setTotalCount(page.totalCount);
        callbacks.current.onLoaded(page, current);
        if (pastLastPage) callbacks.current.onPageRestored(page.page + 1);
        const rows = rowsOf(page);
        const batchGroup = current.batch && page.totalCount === 1 ? rows[0] : undefined;
        const openKey = current.open ?? (batchGroup && keyOf(batchGroup));
        return {
          data: rows.map(row =>
            openKey && keyOf(row) === openKey
              ? { ...row, tableData: { showDetailPanel: renderPanel } }
              : row,
          ),
          page: page.page,
          totalCount: page.totalCount,
        };
      } catch (error) {
        if (stale()) return { data: [], page: 0, totalCount: 0 };
        setTotalCount(0);
        callbacks.current.onFailed(error, apiQuery);
        return { data: [], page: 0, totalCount: 0 };
      }
    },
    [view, load, rowsOf, keyOf, renderPanel, sortFields],
  );

  const fetchRows = useCallback(
    (tableQuery: Query<Row>): Promise<QueryResult<Row>> => {
      const request = fetchPage(tableQuery);
      latestRequest.current = request;
      return settleLatest(latestRequest, request);
    },
    [fetchPage],
  );

  // A click turns the header itself; a sort that arrives through the URL remounts it in that order.
  const sortKey = JSON.stringify(viewSortOf(sortFields, query));
  const shownSort = useRef(sortKey);
  const [header, setHeader] = useState(() => ({ sort: viewSortOf(sortFields, query), mount: 0 }));

  // Remote material-table ignores a new data function, so a filter change pokes it; compared by content.
  const filterKey = filterKeyOf(query);
  const shownFilter = useRef(filterKey);
  useEffect(() => {
    const sortMoved = sortKey !== shownSort.current;
    if (filterKey === shownFilter.current && !sortMoved) return;
    shownFilter.current = filterKey;
    shownSort.current = sortKey;
    // Back or Forward lands on a URL whose page and open row are to be restored, not reset.
    if (history.action === 'POP') restorePending.current = true;
    if (sortMoved) {
      setHeader(shown => ({
        sort: viewSortOf(sortFields, queryRef.current),
        mount: shown.mount + 1,
      }));
    } else {
      tableRef.current?.onQueryChange({ page: 0 });
    }
  }, [filterKey, sortKey, sortFields, history]);

  const onOrderChange = useCallback(
    (_orderBy: number, direction: string, field?: string) => {
      const sort = sortOfField(sortFields, field, direction);
      shownSort.current = JSON.stringify(sort);
      callbacks.current.onSortChange(sort);
    },
    [sortFields],
  );

  const onRowClick = useCallback(
    (_event?: MouseEvent, row?: Row, togglePanel?: unknown) => {
      if (!row || typeof togglePanel !== 'function') return;
      closeOtherPanels(tableRef.current, row, renderPanel);
      togglePanel();
      callbacks.current.onOpenChange(isPanelOpen(row) ? keyOf(row) : null);
    },
    [keyOf, renderPanel],
  );

  // After a write the table asks the server, never the cache, for what it shows.
  const reload = useCallback(() => {
    skipCache.current = true;
    tableRef.current?.onQueryChange();
  }, []);

  return { tableRef, fetchRows, onRowClick, onOrderChange, header, totalCount, reload };
};
